// client/src/api/bookingForm.js
const API_BASE = '/api';

/**
 * Submit a public booking request form
 * @param {Object} data - Form data
 * @returns {Promise<Object>} Response with success status
 */
export const submitBookingRequest = async (data) => {
    try {
        const response = await fetch(`${API_BASE}/booking-requests/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userSlug: data.userSlug,
                clientName: data.clientName,
                clientEmail: data.clientEmail,
                clientPhone: data.clientPhone,
                clientAddress: data.clientAddress,
                projectName: data.projectName,
                serviceDetails: data.serviceDetails,
                preferredDate: data.preferredDate,
                preferredTime: data.preferredTime,
                notes: data.notes
            })
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Failed to submit booking request');
        }

        return {
            success: true,
            data: result
        };
    } catch (error) {
        console.error('Error submitting booking request:', error);
        throw new Error(error.message || 'Failed to submit booking request');
    }
};

/**
 * Fetch user by slug (public endpoint)
 * @param {string} slug - User slug
 * @returns {Promise<Object>} User data
 */
export const fetchUserBySlug = async (slug) => {
    try {
        const response = await fetch(`${API_BASE}/users/public/${slug}`);
        const result = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Failed to fetch user information'
            };
        }

        return {
            success: true,
            user: result.user
        };
    } catch (error) {
        console.error('Error fetching user:', error);
        return {
            success: false,
            message: error.message || 'Failed to fetch user information'
        };
    }
};

/**
 * Get user's booking form settings
 * @returns {Promise<Object>} User's booking form configuration
 */
export const getUserBookingFormSettings = async () => {
    try {
        const response = await fetch(`${API_BASE}/users/booking-form/settings`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Failed to fetch booking form settings');
        }

        return {
            success: true,
            bookingFormEnabled: result.bookingFormEnabled,
            bookingFormSlug: result.bookingFormSlug
        };
    } catch (error) {
        console.error('Error fetching booking form settings:', error);
        throw new Error(error.message || 'Failed to fetch booking form settings');
    }
};

/**
 * Update user's booking form settings
 * @param {Object} settings - Settings to update
 * @returns {Promise<Object>} Updated settings
 */
export const updateUserBookingFormSettings = async (settings) => {
    try {
        const response = await fetch(`${API_BASE}/users/booking-form/settings`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                bookingFormEnabled: settings.bookingFormEnabled,
                bookingFormSlug: settings.bookingFormSlug
            })
        });

        const result = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Failed to update booking form settings'
            };
        }

        return {
            success: true,
            bookingFormEnabled: result.bookingFormEnabled,
            bookingFormSlug: result.bookingFormSlug
        };
    } catch (error) {
        console.error('Error updating booking form settings:', error);
        return {
            success: false,
            message: error.message || 'Failed to update booking form settings'
        };
    }
};
